import { FC } from 'react';
import { useSubscribeToInvalidationState } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import Row from 'components/DepartureLocationListRow';
import Add from 'components/DepartureLocationListAdd';
import Sorting, { useSorting } from 'components/Sorting';

import { useAvoidSuspense, useDelete } from 'lib';

import Query, {
  DepartureLocationListRefetchQuery,
} from '__generated__/DepartureLocationListRefetchQuery.graphql';
import { DepartureLocationsList_data$key } from '__generated__/DepartureLocationsList_data.graphql';

type Props = {
  data: DepartureLocationsList_data$key;
};

const DepartureLocationList: FC<Props> = ({ data }) => {
  const [{ departureLocations }, { refetch, loading }] = useAvoidSuspense<
    DepartureLocationListRefetchQuery,
    DepartureLocationsList_data$key
  >(
    graphql`
      fragment DepartureLocationsList_data on Query
      @refetchable(queryName: "DepartureLocationListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 2147483647 }
        after: { type: "String" }
      ) {
        departureLocations(first: $first, after: $after) {
          __id
          edges {
            node {
              id
              ...DepartureLocationListRow_departureLocation
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionID = departureLocations?.__id;

  const [deleteDepartureLocation, deleting] = useDelete(connectionID);

  const [sort, sorting] = useSorting(
    graphql`
      mutation DepartureLocationsListSortMutation(
        $positions: [EntityWithPositionInput!]!
      ) {
        updateDepartureLocationPositions(positions: $positions)
      }
    `,
    connectionID
  );

  const ids = connectionID ? [connectionID] : [];
  useSubscribeToInvalidationState(ids, () => {
    refetch({});
  });

  const { edges, pageInfo } = departureLocations ?? {};

  if (!edges) return null;

  return (
    <Box>
      <Sorting edges={edges} onSort={sort}>
        <Table
          pageInfo={pageInfo ?? null}
          loading={loading || sorting || deleting}
          icon={<FlightTakeoffIcon />}
          head={
            <TableRow>
              <TableCell sx={{ width: '15px' }} />
              <TableCell>Pavadimas</TableCell>
              <TableCell />
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <Row
              sortDisabled={sorting}
              departureLocation={node}
              key={node.id}
              onDelete={deleteDepartureLocation}
            />
          ))}
        </Table>
      </Sorting>

      {connectionID && (
        <FabDialog fullWidth maxWidth="sm">
          <Add connectionID={connectionID} />
        </FabDialog>
      )}
    </Box>
  );
};

export default DepartureLocationList;
